/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { request } from '@mairistem/tools';

import { BASE_URL_ETATCIVIL } from '@/constants';
import type { Mention, Registre, TypeActe } from '@/types';
import { fileToBase64 } from '@/utils/documentPreview';
import {
  buildActeDecesPayload,
  buildActeMariagePayload,
  buildActeNaissancePayload,
  buildActeReconnaissancePayload,
} from '@/utils/payloads';

type PayloadBuilder = (
  data: Record<string, any>,
  mentions: Mention[],
  selectedRegistre?: Partial<Registre>,
) => Record<string, any>;

/**
 * Un builder par `TypeActe`, fonction pure et testable sans React. `url` est
 * le segment sous `/numerisation-actes` ; `build` produit le payload exact du
 * contrat de l'endpoint.
 *
 * Exporté uniquement pour la checklist d'exhaustivité des types d'acte
 * (`src/__tests__/typesActesImplementes.tsx`) : c'est un des sept registres
 * indexés par `TypeActe`, cf. `constants/typesActesImplementes.ts`.
 */
export const BUILDERS: Partial<
  Record<TypeActe, { url: string; build: PayloadBuilder }>
> = {
  naissance: { url: 'naissance', build: buildActeNaissancePayload },
  reconnaissance: {
    url: 'reconnaissance',
    build: buildActeReconnaissancePayload,
  },
  deces: { url: 'deces', build: buildActeDecesPayload },
  mariage: { url: 'mariage', build: buildActeMariagePayload },
};

/**
 * Hook unique de création d'acte numérisé, réutilisable pour tout type d'acte
 * enregistré dans `BUILDERS` (remplace `useCreateActeNaissance`, spécifique à
 * la naissance).
 *
 * ⚠️ Volontairement générique et *sans* logique par type d'acte à l'intérieur
 * du hook : ce sont les builders (fonctions pures) qui portent la différence,
 * pas le hook lui-même — cf. `assistantConfig.tsx` qui explique pourquoi on ne
 * met pas ce hook dans le registre (règle des hooks).
 */
const useCreateActe = () => {
  const [loading, setLoading] = React.useState(false);

  const createActe = React.useCallback(
    async (
      typeActe: TypeActe | null | undefined,
      data: Record<string, any>,
      mentions: Mention[],
      selectedRegistre?: Partial<Registre>,
      document?: File | null,
    ) => {
      const builder = typeActe ? BUILDERS[typeActe] : undefined;

      if (!builder) {
        return Promise.reject(
          new Error(
            `Aucun endpoint de numérisation connu pour le type d'acte "${typeActe}"`,
          ),
        );
      }

      setLoading(true);

      try {
        const payload = builder.build(data, mentions, selectedRegistre);
        // Document scanné optionnel : le contrat de l'endpoint attend son
        // contenu en base64 SANS le préfixe `data:...;base64,` ajouté par
        // `FileReader.readAsDataURL` (déjà retiré par `fileToBase64`).
        const fichier = document
          ? { nom: document.name, contenu: await fileToBase64(document) }
          : undefined;

        return await request.post(
          `${BASE_URL_ETATCIVIL}/numerisation-actes/${builder.url}`,
          fichier ? { ...payload, fichier } : payload,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createActe, loading };
};

export default useCreateActe;
